import { useRef, useState } from "react";
import {
  updatePreference,
  type PreferenceProfile,
  type ReaderPreference,
} from "../api";
import {
  clearStoredDevicePreference,
  getCurrentReaderDeviceKind,
  getReaderDevicePreferenceName,
  getReaderThemeStorageKey,
  readStoredDevicePreference,
  readStoredDeviceTheme,
  selectReaderDevicePreferenceProfile,
  type ReaderDeviceKind,
  writeStoredDevicePreference,
} from "./reader-device-preference";
import { createPreferenceProfile } from "./reader-preference-api";

const defaultPreference: ReaderPreference = {
  theme: "light",
  fontFamily: "serif",
  fontWeight: 400,
  script: "source",
  fontSize: 18,
  letterSpacing: 0,
  lineHeight: 1.8,
  paragraphSpacing: 20,
  textIndent: 2,
  textAlign: "start",
  textAlignLast: "auto",
  paddingTop: 0,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  contentWidth: 720,
  lightTextColor: "",
  darkTextColor: "",
  lightBackgroundColor: "",
  darkBackgroundColor: "",
  backgroundImageUrl: "",
  autoPage: false,
  prefetchChapterCount: 5,
};
const preferenceSyncIntervalMs = 600;

interface ReaderPreferenceWorkspaceOptions {
  setMessage: (message: string) => void;
}

export function useReaderPreferenceWorkspace({ setMessage }: ReaderPreferenceWorkspaceOptions) {
  const [preferenceProfile, setPreferenceProfile] = useState<PreferenceProfile | null>(null);
  const [preference, setPreference] = useState<ReaderPreference>(() => createInitialPreference());
  const deviceKindRef = useRef<ReaderDeviceKind>(getCurrentReaderDeviceKind());
  const userIdRef = useRef<string | null>(null);
  const preferenceRef = useRef(preference);
  const preferenceProfileRef = useRef<PreferenceProfile | null>(null);
  const pendingPreferenceRef = useRef<ReaderPreference | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncingRef = useRef(false);
  const lastSyncAtRef = useRef(0);

  async function initializePreference(userId: string, activePreference: PreferenceProfile, profiles: PreferenceProfile[]) {
    const deviceKind = deviceKindRef.current;
    userIdRef.current = userId;
    const selected = selectReaderDevicePreferenceProfile(profiles, activePreference, deviceKind);
    const templatePreference = normalizeReaderPreference(selected.profile.preference);
    const storedDraft = readStoredPreferenceDraft(deviceKind, userId);
    const storedTheme = readStoredTheme(deviceKind, userId);
    const requestedPreference = normalizeReaderPreference({
      ...templatePreference,
      ...storedDraft,
      ...(storedTheme ? { theme: storedTheme } : {}),
    });
    const profile = selected.matchedDevice
      ? selected.profile
      : await createPreferenceProfile({
          userId,
          name: getReaderDevicePreferenceName(deviceKind),
          preference: requestedPreference,
          isActive: false,
        });
    const serverPreference = normalizeReaderPreference(profile.preference);
    const nextPreference = normalizeReaderPreference({
      ...serverPreference,
      ...storedDraft,
      ...(storedTheme ? { theme: storedTheme } : {}),
    });

    setPreferenceProfile(profile);
    preferenceProfileRef.current = profile;
    // 旧数据可能缺少新增阅读属性 进入页面时统一补齐默认值
    setPreference(nextPreference);
    preferenceRef.current = nextPreference;
    writeStoredTheme(nextPreference.theme, deviceKind, userId);
    if (!isSameReaderPreference(nextPreference, serverPreference)) {
      writeStoredPreferenceDraft(nextPreference, deviceKind, userId);
      schedulePreferenceSync(nextPreference);
    } else {
      clearStoredPreferenceDraft(deviceKind, userId);
    }
  }

  function handlePreferenceChange(patch: Partial<ReaderPreference>) {
    const next = normalizeReaderPreference({ ...preferenceRef.current, ...patch });

    preferenceRef.current = next;
    setPreference(next);
    writeStoredTheme(next.theme, deviceKindRef.current, userIdRef.current);
    writeStoredPreferenceDraft(next, deviceKindRef.current, userIdRef.current);
    if (!preferenceProfileRef.current) return;
    schedulePreferenceSync(next);
  }

  function clearPreferenceSession() {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = null;
    pendingPreferenceRef.current = null;
    userIdRef.current = null;
    preferenceProfileRef.current = null;
    syncingRef.current = false;
    setPreferenceProfile(null);
  }

  function schedulePreferenceSync(next: ReaderPreference) {
    pendingPreferenceRef.current = next;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);

    const elapsed = Date.now() - lastSyncAtRef.current;
    const wait = Math.max(preferenceSyncIntervalMs - elapsed, 0);
    syncTimerRef.current = setTimeout(() => {
      syncTimerRef.current = null;
      void flushPreferenceSync();
    }, wait);
  }

  async function flushPreferenceSync() {
    const profile = preferenceProfileRef.current;
    const next = pendingPreferenceRef.current;

    if (!profile || !next || syncingRef.current) return;

    pendingPreferenceRef.current = null;
    syncingRef.current = true;
    setMessage("正在同步阅读偏好");

    try {
      const updated = await updatePreference(profile.id, next);
      const normalized = normalizeReaderPreference(updated.preference);

      preferenceProfileRef.current = updated;
      setPreferenceProfile(updated);
      preferenceRef.current = pendingPreferenceRef.current ?? normalized;
      if (!pendingPreferenceRef.current) setPreference(normalized);
      writeStoredTheme(preferenceRef.current.theme, deviceKindRef.current, userIdRef.current);
      if (pendingPreferenceRef.current) {
        writeStoredPreferenceDraft(pendingPreferenceRef.current, deviceKindRef.current, userIdRef.current);
      } else {
        clearStoredPreferenceDraft(deviceKindRef.current, userIdRef.current);
      }
      lastSyncAtRef.current = Date.now();
      setMessage("阅读偏好已同步");
    } catch (error) {
      // 同步期间可能已有更新的待同步偏好 失败重试不能覆盖新值
      if (!pendingPreferenceRef.current) pendingPreferenceRef.current = next;
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      syncingRef.current = false;
      if (pendingPreferenceRef.current) schedulePreferenceSync(pendingPreferenceRef.current);
    }
  }

  return {
    preference,
    initializePreference,
    handlePreferenceChange,
    clearPreferenceSession,
  };
}

function createInitialPreference(): ReaderPreference {
  // 登录页未登录时无法同步服务端 这里用本地主题兜底
  const deviceKind = getCurrentReaderDeviceKind();

  return {
    ...defaultPreference,
    theme: readStoredTheme(deviceKind) ?? defaultPreference.theme,
  };
}

function readStoredTheme(deviceKind: ReaderDeviceKind, userId?: string): ReaderPreference["theme"] | null {
  if (typeof window === "undefined") return null;

  try {
    return readStoredDeviceTheme(window.localStorage, deviceKind, userId);
  } catch {
    return null;
  }
}

function writeStoredTheme(theme: ReaderPreference["theme"], deviceKind: ReaderDeviceKind, userId?: string | null) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(getReaderThemeStorageKey(deviceKind, userId ?? undefined), theme);
  } catch {
    // 忽略不可用的浏览器存储 不影响服务端偏好同步
  }
}

function readStoredPreferenceDraft(deviceKind: ReaderDeviceKind, userId: string): Partial<ReaderPreference> | null {
  if (typeof window === "undefined") return null;

  try {
    return readStoredDevicePreference(window.localStorage, deviceKind, userId);
  } catch {
    return null;
  }
}

function writeStoredPreferenceDraft(preference: ReaderPreference, deviceKind: ReaderDeviceKind, userId?: string | null) {
  if (typeof window === "undefined" || !userId) return;

  try {
    writeStoredDevicePreference(window.localStorage, deviceKind, userId, preference);
  } catch {
    // 本地草稿只用于刷新兜底 写入失败不阻断服务端同步
  }
}

function clearStoredPreferenceDraft(deviceKind: ReaderDeviceKind, userId?: string | null) {
  if (typeof window === "undefined" || !userId) return;

  try {
    clearStoredDevicePreference(window.localStorage, deviceKind, userId);
  } catch {
    // 清理失败时下次初始化仍会通过服务端同步覆盖
  }
}

function normalizeReaderPreference(preference: Partial<ReaderPreference> & { textColor?: string; backgroundColor?: string }): ReaderPreference {
  // 兼容旧版本单套颜色配置 新字段不存在时迁移到浅色配置
  const { textColor, backgroundColor, ...currentPreference } = preference;
  const legacyColors = {
    lightTextColor: currentPreference.lightTextColor ?? textColor ?? defaultPreference.lightTextColor,
    lightBackgroundColor: currentPreference.lightBackgroundColor ?? backgroundColor ?? defaultPreference.lightBackgroundColor,
  };

  return {
    ...defaultPreference,
    ...currentPreference,
    ...legacyColors,
  };
}

function isSameReaderPreference(left: ReaderPreference, right: ReaderPreference): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}
